import { SHOT_OUTCOME_LABELS } from '@golf/common';
import type { ShotOutcome } from '@golf/common';
import {
    ChevronRight, Pencil, PieChart, Play, Target, Trash2, Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Retour } from '../../components/Retour.tsx';
import { ApiError } from '../../lib/api.ts';
import { formatDuration, sessionDuration } from '../../lib/duration.ts';
import { ClubLabel } from '../../components/ClubLabel.tsx';
import { CarteStat, Colonnes, GrandChiffre, JaugeFiabilite, pct } from '../../components/Stat.tsx';
import { modeLabel } from '../../lib/modes.ts';
import type { RunItem } from '../../lib/planning.ts';
import { etapeFaite, getCurrentRun } from '../../lib/planning.ts';
import { useSettings } from '../../lib/settings.tsx';
import { formatDistance, fromMetres } from '../../lib/units.ts';
import type { Summary } from '../../lib/training.ts';
import {
    deleteSession, fromLocalInput, getSummary, patchSession, toLocalInput,
} from '../../lib/training.ts';
import type { ComparisonRow } from './ComparisonBars.tsx';

const FAULTS: { key: Exclude<ShotOutcome, 'center'>; cur: keyof Summary['clubs'][number]; prev: keyof Summary['clubs'][number] }[] = [
    { key: 'slice', cur: 'cntSlice', prev: 'prevCntSlice' },
    { key: 'hook', cur: 'cntHook', prev: 'prevCntHook' },
    { key: 'top', cur: 'cntTop', prev: 'prevCntTop' },
    { key: 'fat', cur: 'cntFat', prev: 'prevCntFat' },
    { key: 'shank', cur: 'cntShank', prev: 'prevCntShank' },
    { key: 'miss', cur: 'cntMiss', prev: 'prevCntMiss' },
];

export function SessionSummary() {
    const { id } = useParams<{ id: string }>();
    const { unit } = useSettings();
    const navigate = useNavigate();
    const [summary, setSummary] = useState<Summary | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [editingDate, setEditingDate] = useState(false);
    const [dateDraft, setDateDraft] = useState('');
    const [endDraft, setEndDraft] = useState('');
    const [editingNote, setEditingNote] = useState(false);
    const [noteDraft, setNoteDraft] = useState('');
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [busy, setBusy] = useState(false);

    const reload = useCallback(async () => {
        if (!id) return;
        try {
            const s = await getSummary(id);
            setSummary(s);
            setDateDraft(toLocalInput(s.session.startedAt));
            setEndDraft(s.session.endedAt ? toLocalInput(s.session.endedAt) : '');
            setNoteDraft(s.session.notes ?? '');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Chargement impossible');
        }
    }, [id]);

    useEffect(() => { void reload(); }, [reload]);

    /** Étape suivante de la séance en cours, s'il en reste une.
     *
     *  Chargé à part de la synthèse : l'immense majorité des exercices ne
     *  s'inscrivent dans aucun plan, et une erreur ici ne doit pas priver le
     *  joueur de ses statistiques. D'où le `catch` muet.
     *
     *  Et seulement si l'exercice AFFICHÉ appartient à cette séance : sans
     *  cette condition, ouvrir une synthèse de la semaine dernière pendant
     *  qu'un plan est en cours annonçait une « suite » à un exercice qui n'y
     *  était pour rien. */
    const [nextStep, setNextStep] = useState<RunItem | null>(null);
    useEffect(() => {
        void getCurrentRun()
            .then((r) => {
                const belongs = r.items.some((i) => i.sessionId === id);
                setNextStep(belongs
                    ? r.items.find((i) => !etapeFaite(i)) ?? null
                    : null);
            })
            .catch(() => setNextStep(null));
    }, [id]);

    /** Déplacer le début emmène la fin avec lui.
     *
     *  C'est le geste le plus courant — on a oublié de démarrer la séance, on
     *  la recale d'une heure — et il ne doit surtout pas changer la durée. La
     *  fin reste modifiable ensuite, pour le cas inverse : celui où l'on a
     *  oublié d'arrêter. */
    const shiftStart = (value: string): void => {
        if (endDraft && dateDraft) {
            const delta = new Date(value).getTime() - new Date(dateDraft).getTime();
            if (Number.isFinite(delta)) {
                setEndDraft(toLocalInput(new Date(new Date(endDraft).getTime() + delta).toISOString()));
            }
        }
        setDateDraft(value);
    };

    /** La durée du brouillon, ou null si les bornes sont dans le désordre —
     *  ce qui bloque l'enregistrement plutôt que de laisser le serveur refuser. */
    const draftDuration = (() => {
        if (!dateDraft) return null;
        if (!endDraft) return null;
        const ms = new Date(endDraft).getTime() - new Date(dateDraft).getTime();
        return Number.isFinite(ms) && ms >= 0 ? formatDuration(ms) : null;
    })();

    const saveDate = async (): Promise<void> => {
        if (!id || !dateDraft) return;
        setBusy(true); setError(null);
        try {
            await patchSession(id, {
                startedAt: fromLocalInput(dateDraft),
                ...(endDraft ? { endedAt: fromLocalInput(endDraft) } : {}),
            });
            setEditingDate(false);
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Modification impossible');
        } finally { setBusy(false); }
    };

    const saveNote = async (): Promise<void> => {
        if (!id) return;
        setBusy(true); setError(null);
        try {
            // Vidé volontairement : on efface la note plutôt que d'enregistrer
            // une chaîne blanche, qui s'afficherait comme des guillemets vides.
            await patchSession(id, { notes: noteDraft.trim() || null });
            setEditingNote(false);
            await reload();
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Modification impossible');
        } finally { setBusy(false); }
    };

    const remove = async (): Promise<void> => {
        if (!id) return;
        setBusy(true); setError(null);
        try {
            await deleteSession(id);
            void navigate('/entrainement/historique');
        } catch (e) {
            setError(e instanceof ApiError ? e.message : 'Suppression impossible');
            setBusy(false);
        }
    };

    if (error && !summary) {
        return <div role="alert" className="alert alert-error mt-4"><span>{error}</span></div>;
    }
    if (!summary) {
        return <div className="flex justify-center py-16"><span className="loading loading-ring loading-lg text-primary" /></div>;
    }

    const { session, clubs, totals } = summary;
    const duration = sessionDuration(session.startedAt, session.endedAt);

    // Répartition des fautes, tous clubs confondus. Le total par type dit où
    // le travail doit porter ; le détail par club vit dans les cartes.
    const faultRows: ComparisonRow[] = FAULTS.map(({ key, cur, prev }) => {
        const current = clubs.reduce((s, c) => s + Number(c[cur] ?? 0), 0);
        const hasPrev = clubs.some((c) => c[prev] != null);
        return {
            key,
            label: SHOT_OUTCOME_LABELS[key],
            current,
            previous: hasPrev ? clubs.reduce((s, c) => s + Number(c[prev] ?? 0), 0) : null,
        };
    }).filter((r) => r.current > 0 || (r.previous ?? 0) > 0);

    /* L'échelle des fautes part du plus haut bâton, sans plancher.
     *
     * Il y en avait un à quatre, hérité des barres horizontales : un axe fixe y
     * gardait deux séances comparables d'un coup d'œil. En colonnes, il aplatit
     * tout — deux slices sur une séance propre dessinaient un trait de six
     * pixels, et la faute dominante ne se détachait plus de rien. Le repère
     * pointé de la séance précédente entre dans la même échelle, donc la
     * comparaison tient toujours — elle se lit dans la colonne, plus sur un axe
     * commun à deux écrans. */
    const maxFault = Math.max(1, ...faultRows.map((r) => Math.max(r.current, r.previous ?? 0)));
    const anyPrevious = clubs.some((c) => c.prevSuccessRate != null);

    return (
        <div className="space-y-6 py-2">
            <header>
                {/* On arrive ici de cinq endroits — le menu, l'historique,
                    le déroulé d'une séance, son bilan, les statistiques. Le
                    lien renvoyait toujours au menu, ce qui faisait perdre sa
                    page à quatre visiteurs sur cinq. */}
                <Retour defaut={{ to: '/entrainement', label: 'Entraînement' }} />
                {/* Le mode en titre : depuis qu'il y en a trois, « Synthèse de
                    séance » ne disait plus laquelle on regarde. */}
                <h1 className="font-display text-2xl font-bold tracking-tight">
                    {session.exerciseName ?? modeLabel(session.mode)}
                </h1>

                {/* La suite de la séance, s'il y en a une.
                    Sans ce rappel, terminer une étape déposait le joueur sur
                    une synthèse sans issue : il fallait deviner qu'il restait
                    du plan et repasser par le menu. Au practice, entre deux
                    seaux, on ne devine pas — on suit ce qui est écrit. */}
                {nextStep && (
                    <Link to="/entrainement/seance-en-cours"
                        className="mt-3 flex items-center gap-3 rounded-box border-2 border-primary bg-primary/10 p-3">
                        <Play size={20} className="shrink-0 text-primary" />
                        <span className="min-w-0 flex-1">
                            <span className="block text-sm font-bold">Suite de la séance</span>
                            <span className="block truncate text-xs opacity-70">
                                {nextStep.exerciseName ?? modeLabel(nextStep.mode ?? '')}
                            </span>
                        </span>
                        <ChevronRight size={18} className="shrink-0 opacity-50" />
                    </Link>
                )}

                {editingDate ? (
                    <div className="mt-2 space-y-3 rounded-box bg-base-200 p-3">
                        <label className="block">
                            <span className="mb-1 block text-sm">Début</span>
                            <input
                                type="datetime-local"
                                className="input input-bordered w-full"
                                value={dateDraft}
                                onChange={(e) => shiftStart(e.target.value)}
                            />
                        </label>

                        {/* La fin, modifiable elle aussi.
                            La durée n'est pas stockée : elle se déduit des deux
                            bornes. Sans ce champ, une séance dont on a oublié
                            d'appuyer sur « Arrêter » affichait trois heures
                            pour quarante minutes, et rien ne permettait de la
                            corriger. */}
                        {session.endedAt && (
                            <label className="block">
                                <span className="mb-1 block text-sm">Fin</span>
                                <input
                                    type="datetime-local"
                                    className="input input-bordered w-full"
                                    value={endDraft}
                                    min={dateDraft}
                                    onChange={(e) => setEndDraft(e.target.value)}
                                />
                            </label>
                        )}

                        {/* La durée qui en résulte, recalculée à la frappe :
                            c'est elle qu'on vient corriger, elle doit être
                            visible pendant qu'on s'y reprend. */}
                        <p className={`text-sm ${draftDuration ? 'opacity-70' : 'text-error'}`}>
                            {draftDuration
                                ? `Durée : ${draftDuration}`
                                : 'La fin ne peut pas précéder le début.'}
                        </p>
                        {session.endedAt && (
                            <p className="text-xs opacity-50">
                                Déplacer le début décale la fin d'autant — la durée est
                                conservée tant que vous ne touchez pas à la fin.
                            </p>
                        )}

                        <div className="flex gap-2">
                            <button type="button" className="btn btn-ghost btn-sm flex-1"
                                onClick={() => setEditingDate(false)}>Annuler</button>
                            <button type="button" className="btn btn-primary btn-sm flex-1"
                                disabled={busy || !draftDuration}
                                onClick={() => void saveDate()}>Enregistrer</button>
                        </div>
                    </div>
                ) : (
                    <button type="button" onClick={() => setEditingDate(true)}
                        className="mt-1 flex items-center gap-1.5 text-left text-sm opacity-60">
                        <span>
                            {new Date(session.startedAt).toLocaleString('fr-FR', {
                                dateStyle: 'full', timeStyle: 'short',
                            })}
                            {duration && ` · ${duration}`}
                            {session.location && ` · ${session.location}`}
                        </span>
                        <Pencil size={13} className="shrink-0" />
                    </button>
                )}

                {/* Le mémo de la séance, relisible et corrigeable.
                    En mode libre c'est la définition de l'exercice ; ailleurs
                    c'est une note facultative. Un mémo qu'on ne peut pas
                    rectifier après coup finit par ne plus être écrit. */}
                {editingNote ? (
                    <div className="mt-3 space-y-2 rounded-box bg-base-200 p-3">
                        <label className="block">
                            <span className="mb-1 block text-sm">Votre exercice</span>
                            <input
                                type="text"
                                maxLength={200}
                                autoFocus
                                className="input input-bordered w-full"
                                placeholder="Pause au backswing"
                                value={noteDraft}
                                onChange={(e) => setNoteDraft(e.target.value)}
                            />
                        </label>
                        <div className="flex gap-2">
                            <button type="button" className="btn btn-ghost btn-sm flex-1"
                                onClick={() => {
                                    setNoteDraft(session.notes ?? '');
                                    setEditingNote(false);
                                }}>Annuler</button>
                            <button type="button" className="btn btn-primary btn-sm flex-1"
                                disabled={busy} onClick={() => void saveNote()}>Enregistrer</button>
                        </div>
                    </div>
                ) : (
                    <button type="button" onClick={() => setEditingNote(true)}
                        className="mt-2 flex w-full items-center gap-1.5 text-left text-sm">
                        <span className={session.notes ? 'italic' : 'opacity-50'}>
                            {session.notes ? `« ${session.notes} »` : 'Ajouter une note'}
                        </span>
                        <Pencil size={13} className="shrink-0 opacity-60" />
                    </button>
                )}

                {error && (
                    <div role="alert" className="alert alert-error mt-3 text-sm"><span>{error}</span></div>
                )}
            </header>

            {/* Le même trio de grands chiffres que partout ailleurs. Trois
                tuiles isolées disaient la même chose sous une autre forme, à
                deux écrans de la carte qui, elle, porte un titre. */}
            <CarteStat icone={<Target size={13} aria-hidden="true" />} titre="Cette séance">
                <div className="grid grid-cols-3 gap-2 text-center">
                    <GrandChiffre mot="de réussite" ton="var(--color-primary)"
                        valeur={totals.successRate != null ? pct(totals.successRate) : null} />
                    <GrandChiffre valeur={String(totals.shots)}
                        mot={totals.shots > 1 ? 'balles' : 'balle'} />
                    <GrandChiffre valeur={String(totals.series)}
                        mot={totals.series > 1 ? 'séries' : 'série'} />
                </div>
            </CarteStat>

            {clubs.length === 0 ? (
                <p className="rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                    Aucune balle enregistrée sur cette séance.
                </p>
            ) : (
                <>
                    {/* Une JAUGE par club, et non des barres de comparaison.
                        Un taux se lit sur sa pleine échelle — c'est la même
                        jauge, aux mêmes seuils et aux mêmes couleurs, que dans
                        les statistiques et dans Mon matériel. Le club change
                        d'écran, pas de couleur.

                        Le pointillé dit où en était la séance précédente. */}
                    <CarteStat icone={<Pencil size={13} aria-hidden="true" />}
                        titre="Taux de réussite par club"
                        note={anyPrevious
                            ? "L'écart avec la séance précédente est donné club par club, plus bas"
                            : 'Première séance avec ces clubs : pas encore de comparaison'}>
                        <div className="space-y-2">
                            {clubs.map((c) => (
                                <div key={c.clubId} className="flex items-center gap-3">
                                    <span className="w-16 shrink-0 truncate text-sm font-bold">{c.label}</span>
                                    <JaugeFiabilite taux={c.successRate ?? 0} />
                                    <span className="font-score w-12 shrink-0 text-right text-base tabular-nums">
                                        {c.successRate != null ? pct(c.successRate) : '—'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </CarteStat>

                    {faultRows.length > 0 && (
                        <CarteStat icone={<PieChart size={13} aria-hidden="true" />}
                            titre="Répartition des fautes"
                            note={`Tous clubs confondus, en nombre de balles${
                                anyPrevious ? ' · le pointillé, c\u2019est la séance précédente' : ''}`}>
                            <Colonnes hauteur="5rem" colonnes={faultRows.map((r) => ({
                                cle: r.key,
                                mot: r.label.toLowerCase(),
                                valeur: String(r.current),
                                part: r.current / maxFault,
                                /* UNE teinte : les fautes ne se classent pas de
                                   la meilleure à la pire, elles se nomment. Seule
                                   la plus coûteuse se détache. */
                                ton: r.current >= maxFault ? 'var(--brique)' : 'var(--tuile)',
                                avant: r.previous != null ? r.previous / maxFault : null,
                            }))} />
                        </CarteStat>
                    )}

                    {/* UNE carte, une ligne sur sable par club — comme le sac
                        dans Mon matériel. C'étaient autant de cartes grises que
                        de clubs, avec autant de cadres : le sac se lit d'une
                        traite, il n'est pas six sujets distincts. */}
                    <CarteStat icone={<Wrench size={13} aria-hidden="true" />} titre="Détail par club">
                        <div className="space-y-2">
                            {clubs.map((c) => {
                                const delta = c.successRate != null && c.prevSuccessRate != null
                                    ? c.successRate - c.prevSuccessRate : null;
                                return (
                                    <div key={c.clubId} className="rounded-field p-2.5"
                                        style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                                        <div className="flex items-baseline justify-between gap-3">
                                            <ClubLabel club={c} />
                                            <span className="shrink-0 whitespace-nowrap">
                                                <span className="font-score text-lg tabular-nums">
                                                    {c.successRate != null ? pct(c.successRate) : '\u2014'}
                                                </span>
                                                {delta != null && Math.abs(delta) >= 0.1 && (
                                                    <span className="ml-1.5 text-xs font-bold tabular-nums"
                                                        style={{
                                                            color: delta >= 0
                                                                ? 'var(--color-primary)' : 'var(--brique-bord)',
                                                        }}>
                                                        {delta >= 0 ? '\u25b2\u202f' : '\u25bc\u202f'}
                                                        {pct(Math.abs(delta)).replace('\u00a0%', '')}
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                        <div className="mt-1 text-xs opacity-75">
                                            {c.cntCenter}/{c.shotsTotal} réussies · {c.seriesCount} série
                                            {c.seriesCount > 1 ? 's' : ''}
                                            {c.distanceAvgM != null && (
                                                <> · {formatDistance(c.distanceAvgM, unit)} en moyenne
                                                    {' ('}{formatDistance(c.distanceMinM, unit, { withUnit: false })}
                                                    –{formatDistance(c.distanceMaxM, unit, { withUnit: false })}{')'}
                                                </>
                                            )}
                                        </div>

                                        {/* Se situer : ce que le club est censé
                                            porter, ce qu'il porte d'habitude entre
                                            ces mains-là, et ce qu'il a porté
                                            aujourd'hui. Un écart de 8 m sous la
                                            référence n'est pas une contre-performance
                                            si c'est la norme du joueur — d'où les
                                            deux repères plutôt qu'un. */}
                                        {c.distanceAvgM != null
                                            && (c.referenceDistanceM != null || c.careerDistanceM != null) && (
                                            <div className="mt-1.5 flex flex-wrap gap-1.5 text-[11px]">
                                                {([
                                                    ['vs annoncé', c.referenceDistanceM],
                                                    ['vs votre habitude', c.careerDistanceM],
                                                ] as const).filter(([, m]) => m != null).map(([mot, m]) => (
                                                    <span key={mot} className="rounded-field px-1.5 py-0.5 font-semibold"
                                                        style={{ backgroundColor: 'var(--color-base-100)' }}>
                                                        {mot} {formatDistance(m!, unit)}{' '}
                                                        <span style={{
                                                            color: c.distanceAvgM! >= m!
                                                                ? 'var(--color-primary)' : 'var(--brass-ink)',
                                                        }}>
                                                            {c.distanceAvgM! >= m! ? '+' : '\u2212'}
                                                            {Math.abs(fromMetres(c.distanceAvgM!, unit)
                                                                - fromMetres(m!, unit))}
                                                        </span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="mt-1.5 flex flex-wrap gap-1">
                                            {FAULTS.map(({ key, cur }) => {
                                                const n = Number(c[cur] ?? 0);
                                                return n > 0 ? (
                                                    <span key={key}
                                                        className="rounded-field px-1.5 py-0.5 text-[11px] font-semibold"
                                                        style={{
                                                            backgroundColor: 'var(--brique)',
                                                            color: 'var(--brique-encre)',
                                                        }}>
                                                        {SHOT_OUTCOME_LABELS[key]} ×{n}
                                                    </span>
                                                ) : null;
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CarteStat>
                </>
            )}

            {/* La suppression est en bas, après tout ce qu'elle détruit : on
                la trouve en la cherchant, pas en effleurant l'écran. */}
            <section className="pt-4">
                <button type="button" className="btn btn-ghost btn-sm gap-2 text-error"
                    onClick={() => setConfirmDelete(true)}>
                    <Trash2 size={16} /> Supprimer cette séance
                </button>
            </section>

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-sm rounded-box bg-base-200 p-5">
                        <h2 className="text-lg font-bold">Supprimer cette séance ?</h2>
                        <p className="mt-2 text-sm opacity-80">
                            {new Date(session.startedAt).toLocaleDateString('fr-FR', {
                                weekday: 'long', day: 'numeric', month: 'long',
                            })}
                            {' — '}{totals.shots} balle{totals.shots > 1 ? 's' : ''} sur{' '}
                            {totals.series} série{totals.series > 1 ? 's' : ''}.
                        </p>
                        <p className="mt-2 text-xs opacity-60">
                            Les balles et les statistiques de cette séance disparaissent
                            définitivement. Vos comparaisons repartiront sur la séance
                            précédente.
                        </p>
                        <div className="mt-4 space-y-2">
                            <button type="button" disabled={busy} onClick={() => void remove()}
                                className="btn btn-error btn-block gap-2 tap-target">
                                <Trash2 size={18} /> Supprimer définitivement
                            </button>
                            <button type="button" className="btn btn-ghost btn-block"
                                onClick={() => setConfirmDelete(false)}>Annuler</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
